import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { matchRoute } from "@/routes";
import {
  LAST_SCENE_STORAGE_KEY,
  THEME_STORAGE_KEY,
  SKY_STORAGE_KEY,
  applyScene,
  chooseScene,
  isSceneKey,
  readSkySetting,
  readThemeSetting,
  resolveTheme,
  skyImageUrl,
} from "@/ui/sky/scene";
import type { SceneKey, SkyTheme } from "@/ui/sky/sky";
import { SKY_SETTINGS_EVENT } from "@/ui/sky/preferences";

/**
 * The pixel sky behind the whole app, and the runtime that picks what it shows.
 *
 * Mounted inside the screen bezel, with a separate instance on the login screen. On every route
 * entry it picks a scene (random and never the previous one, unless
 * `localStorage['busee.sky']` pins one), resolves day or night from
 * `prefers-color-scheme` (unless `busee.theme` pins it), and writes the scene's
 * tokens onto <html> (`ui/sky/scene.ts`). Nothing re-renders: the component
 * renders two empty layers and everything else is style writes.
 *
 * **Cross-fade, opacity only.** The two layers stack. The incoming scene is
 * painted on the idle layer, which sits above and fades in over 600ms; when it
 * lands, the old layer is switched off with no transition. Nothing in the tree
 * above the cards animates, so no blurred surface ever sits under a changing
 * ancestor (PIXEL-SKY-DESIGN.md §Motion). Reduced motion removes the transition
 * in CSS (`.sky-layer`), so the swap is instant.
 *
 * **Kanban City** draws no sky: its 3D sea is its own, so the layers are
 * cleared and `--busee-sky-image` is `none` there.
 */

type Layers = { els: [HTMLDivElement, HTMLDivElement]; front: 0 | 1; url: string | null; fade: number | null };

const FADE_MS = 600;

/** `localStorage` itself can throw on access (blocked site data); treat that as empty. */
function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Switches off the layer that lost, instantly, once the incoming one is opaque. */
function settle(root: HTMLDivElement, layers: Layers): void {
  if (layers.fade === null) {
    return;
  }
  window.clearTimeout(layers.fade);
  layers.fade = null;
  const outgoing = layers.els[layers.front];
  outgoing.removeAttribute("data-on");
  outgoing.style.backgroundImage = "";
  layers.front = layers.front === 0 ? 1 : 0;
  // The CSS fallback image has done its job once a layer owns the picture.
  root.dataset.ready = "";
}

function showSky(root: HTMLDivElement, layers: Layers, url: string | null): void {
  settle(root, layers);
  if (url === layers.url) {
    return;
  }
  layers.url = url;
  if (url === null) {
    for (const el of layers.els) {
      el.removeAttribute("data-on");
      el.style.backgroundImage = "";
    }
    delete root.dataset.ready;
    return;
  }

  const incoming = layers.els[layers.front === 0 ? 1 : 0];
  const outgoing = layers.els[layers.front];
  incoming.style.backgroundImage = `url(${url})`;
  incoming.style.zIndex = "2";
  outgoing.style.zIndex = "1";

  const start = () => {
    // A newer scene may have taken over while the image decoded.
    if (layers.url !== url) {
      return;
    }
    incoming.setAttribute("data-on", "");
    layers.fade = window.setTimeout(() => settle(root, layers), FADE_MS + 40);
  };
  const image = new Image();
  image.src = url;
  image.decode().then(start, start);
}

export function SkyBackdrop() {
  const { pathname } = useLocation();
  const matched = matchRoute(pathname);
  // The same key AppShell uses to replay the page entrance: `/tasks/:date`
  // changes within one section keep the scene, a change of section picks anew.
  const routeKey = matched?.path ?? pathname;
  const sky = matched?.path !== "kanban-city";

  const rootRef = useRef<HTMLDivElement>(null);
  const aRef = useRef<HTMLDivElement>(null);
  const bRef = useRef<HTMLDivElement>(null);
  const layers = useRef<Layers | null>(null);
  const scene = useRef<SceneKey | null>(null);
  const setting = useRef(readSkySetting(local()));
  const skyOn = useRef(sky);
  skyOn.current = sky;

  const paint = (key: SceneKey, theme: SkyTheme) => {
    const root = rootRef.current;
    const els = aRef.current && bRef.current ? ([aRef.current, bRef.current] as const) : null;
    if (!root || !els) {
      return;
    }
    layers.current ??= { els: [els[0], els[1]], front: 0, url: null, fade: null };
    applyScene(document.documentElement, key, theme, { sky: skyOn.current });
    showSky(root, layers.current, skyOn.current ? skyImageUrl(key, theme) : null);
  };

  // One scene per route entry. `routeKey` and `sky` are the only inputs; the
  // random pick must not re-run for any other re-render.
  useEffect(() => {
    let last: SceneKey | null = null;
    try {
      const stored = sessionStorage.getItem(LAST_SCENE_STORAGE_KEY);
      last = isSceneKey(stored) ? stored : null;
    } catch {
      last = null;
    }
    setting.current = readSkySetting(local());
    const key = chooseScene(setting.current, last);
    scene.current = key;
    try {
      sessionStorage.setItem(LAST_SCENE_STORAGE_KEY, key);
    } catch {
      // Private window: the next pick may repeat, which is harmless.
    }
    const dark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    paint(key, resolveTheme(readThemeSetting(local()), dark));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, sky]);

  // Day and night follow the system, and a settings change (P4) from another
  // window, without picking a new scene.
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const refresh = () => {
      if (scene.current) {
        paint(scene.current, resolveTheme(readThemeSetting(local()), query.matches));
      }
    };
    const onSettings = () => {
      const next = readSkySetting(local());
      if (next !== setting.current) {
        scene.current = chooseScene(next, scene.current);
        setting.current = next;
      }
      if (scene.current) {
        try { sessionStorage.setItem(LAST_SCENE_STORAGE_KEY, scene.current); } catch { /* Optional persistence. */ }
      }
      refresh();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY || event.key === SKY_STORAGE_KEY || event.key === null) onSettings();
    };
    query.addEventListener("change", refresh);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SKY_SETTINGS_EVENT, onSettings);
    return () => {
      query.removeEventListener("change", refresh);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SKY_SETTINGS_EVENT, onSettings);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (layers.current?.fade !== null && layers.current?.fade !== undefined) window.clearTimeout(layers.current.fade);
    if (layers.current) layers.current.url = null;
  }, []);

  return (
    <div ref={rootRef} className="sky" aria-hidden="true" data-testid="sky">
      <div ref={aRef} className="sky-layer" />
      <div ref={bRef} className="sky-layer" />
    </div>
  );
}
